import { useState, useEffect } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { api } from '../../../api';

import { dobToMySQL, formatDate, emptyProperty, emptyAuto, emptyRecUnit, getAuthToken } from './helpers';

import HeaderBar from './sections/HeaderBar';
import AnalyzeModals from './sections/AnalyzeModals';
import ClientInfoSection from './sections/ClientInfoSection';
import PropertiesSection from './sections/PropertiesSection';
import VehiclesSection from './sections/VehiclesSection';
import DriversSection from './sections/DriversSection';
import RecreationalSection from './sections/RecreationalSection';
import LiabilitySection from './sections/LiabilitySection';
import DocumentsSection from './sections/DocumentsSection';

// ─────────────────────────────────────────
// CLIENT DETAIL — ORCHESTRATOR
// Owns all state, all handlers, the data-loading useEffect.
// Renders the 7 sections in order, passing data + handlers as props.
// ─────────────────────────────────────────
export default function ClientDetail({ clientId, onBack }) {
  // ── Top-level data + UI state ──
  const [client, setClient] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [files, setFiles] = useState([]);

  // ── Upload + analyze ──
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [pendingFile, setPendingFile] = useState(null);
  const [analyzingFileId, setAnalyzingFileId] = useState(null);
  const [analyzeData, setAnalyzeData] = useState(null);

  // ── Resend intake ──
  const [showResendForm, setShowResendForm] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendingIntake, setResendingIntake] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // ── Per-section editing state ──
  const [editingBasic, setEditingBasic] = useState(false);
  const [basicDraft, setBasicDraft] = useState({});

  const [editingLiability, setEditingLiability] = useState(false);
  const [liabilityDraft, setLiabilityDraft] = useState({});

  const [properties, setProperties] = useState([]);
  const [editingPropertyId, setEditingPropertyId] = useState(null);
  const [propertyDraft, setPropertyDraft] = useState({});
  const [propertyLosses, setPropertyLosses] = useState({});

  const [autos, setAutos] = useState([]);
  const [editingAutoId, setEditingAutoId] = useState(null);
  const [autoDraft, setAutoDraft] = useState({});

  const [recreationalUnits, setRecreationalUnits] = useState([]);
  const [editingRecId, setEditingRecId] = useState(null);
  const [recDraft, setRecDraft] = useState({});

  const [drivers, setDrivers] = useState([]);
  const [editingDriverId, setEditingDriverId] = useState(null);
  const [driverDraft, setDriverDraft] = useState({});

  // ─────────────────────────────────────────
  // INITIAL LOAD — client, users, files, prior losses
  // ─────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      api(`/api/clients/${clientId}`),
      api('/api/users'),
      api(`/api/clients/${clientId}/files`).catch(() => []),
    ]).then(([clientData, usersData, filesData]) => {
      setClient(clientData);
      setUsers(usersData);
      setFiles(filesData || []);
      const draftData = {
        ...clientData,
        dateOfBirth: formatDate(clientData.dateOfBirth) || '',
        spouseDOB: formatDate(clientData.spouseDOB) || '',
        gender: clientData.gender || '',
      };
      setBasicDraft(draftData);
      setLiabilityDraft(clientData);
      setResendEmail(clientData.email || '');
      const props = clientData.properties || [];
      setProperties(props);
      setAutos(clientData.autos || []);
      setRecreationalUnits(clientData.recreationalUnits || []);
      setDrivers(clientData.drivers || []);
      if (props.length > 0) {
        Promise.all(
          props.map(p => api(`/api/clients/${clientId}/properties/${p.id}/losses`).catch(() => []))
        ).then(lossArrays => {
          const map = {};
          props.forEach((p, i) => { map[p.id] = lossArrays[i] || []; });
          setPropertyLosses(map);
        });
      }
    }).catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [clientId]);

  // ─────────────────────────────────────────
  // CLIENT INFO HANDLERS
  // ─────────────────────────────────────────
  const saveBasic = async () => {
    setSaving(true);
    try {
      const payload = { ...basicDraft, dateOfBirth: dobToMySQL(basicDraft.dateOfBirth), spouseDOB: dobToMySQL(basicDraft.spouseDOB) };
      await api(`/api/clients/${clientId}`, { method: 'PATCH', body: JSON.stringify(payload) });
      setClient(p => ({ ...p, ...payload }));
      setEditingBasic(false);
    } catch { setError('Save failed'); }
    finally { setSaving(false); }
  };

  // ─────────────────────────────────────────
  // LIABILITY HANDLERS
  // ─────────────────────────────────────────
  const saveLiability = async () => {
    setSaving(true);
    try {
      await api(`/api/clients/${clientId}/liability`, { method: 'PATCH', body: JSON.stringify(liabilityDraft) });
      setClient(p => ({ ...p, ...liabilityDraft }));
      setEditingLiability(false);
    } catch { setError('Save failed'); }
    finally { setSaving(false); }
  };

  // ─────────────────────────────────────────
  // PROPERTY HANDLERS
  // ─────────────────────────────────────────
  const addProperty = async () => {
    try {
      const created = await api(`/api/clients/${clientId}/properties`, { method: 'POST', body: JSON.stringify(emptyProperty()) });
      setProperties(p => [...p, created]);
      setPropertyLosses(prev => ({ ...prev, [created.id]: [] }));
      setEditingPropertyId(created.id);
      setPropertyDraft(created);
    } catch { setError('Failed to add property'); }
  };

  const startEditProperty = (prop) => {
    setEditingPropertyId(prop.id);
    setPropertyDraft({ ...prop });
  };

  const saveProperty = async (id) => {
    setSaving(true);
    try {
      await api(`/api/clients/${clientId}/properties/${id}`, { method: 'PATCH', body: JSON.stringify(propertyDraft) });
      setProperties(p => p.map(x => x.id === id ? { ...x, ...propertyDraft } : x));
      setEditingPropertyId(null);
    } catch { setError('Save failed'); }
    finally { setSaving(false); }
  };

  const cancelEditProperty = () => setEditingPropertyId(null);

  const deleteProperty = async (id) => {
    try {
      await api(`/api/clients/${clientId}/properties/${id}`, { method: 'DELETE' });
      setProperties(p => p.filter(x => x.id !== id));
      setPropertyLosses(prev => { const next = { ...prev }; delete next[id]; return next; });
    } catch { setError('Failed to delete property'); }
  };

  // ─────────────────────────────────────────
  // PRIOR LOSS HANDLERS
  // ─────────────────────────────────────────
  const addLoss = async (propertyId) => {
    try {
      const created = await api(`/api/clients/${clientId}/properties/${propertyId}/losses`,
        { method: 'POST', body: JSON.stringify({ lossDate: null, lossType: '', source: '', amountPaid: null, claimClosed: null, catClaim: false }) });
      setPropertyLosses(prev => ({ ...prev, [propertyId]: [...(prev[propertyId] || []), created] }));
      setProperties(prev => prev.map(p => p.id === propertyId ? { ...p, hasPriorLosses: true } : p));
    } catch { setError('Failed to add loss'); }
  };

  const updateLossField = (propertyId, lossId, key, value) => {
    setPropertyLosses(prev => ({
      ...prev,
      [propertyId]: (prev[propertyId] || []).map(l => l.id === lossId ? { ...l, [key]: value } : l),
    }));
  };

  const saveLoss = async (propertyId, lossId) => {
    const loss = (propertyLosses[propertyId] || []).find(l => l.id === lossId);
    if (!loss) return;
    try {
      await api(`/api/clients/${clientId}/properties/${propertyId}/losses/${lossId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          lossDate: loss.lossDate || null,
          lossType: loss.lossType || null,
          source: loss.source || null,
          amountPaid: loss.amountPaid != null ? loss.amountPaid : null,
          claimClosed: loss.claimClosed != null ? loss.claimClosed : null,
          catClaim: loss.catClaim ? true : false,
        }),
      });
    } catch { setError('Failed to save loss'); }
  };

  const deleteLoss = async (propertyId, lossId) => {
    try {
      await api(`/api/clients/${clientId}/properties/${propertyId}/losses/${lossId}`, { method: 'DELETE' });
      const remaining = (propertyLosses[propertyId] || []).filter(l => l.id !== lossId);
      setPropertyLosses(prev => ({ ...prev, [propertyId]: remaining }));
      if (remaining.length === 0) {
        setProperties(prev => prev.map(p => p.id === propertyId ? { ...p, hasPriorLosses: false } : p));
      }
    } catch { setError('Failed to delete loss'); }
  };

  // ─────────────────────────────────────────
  // AUTO HANDLERS
  // ─────────────────────────────────────────
  const addAuto = async () => {
    try {
      const created = await api(`/api/clients/${clientId}/autos`, { method: 'POST', body: JSON.stringify(emptyAuto()) });
      setAutos(p => [...p, created]);
      setEditingAutoId(created.id);
      setAutoDraft(created);
    } catch { setError('Failed to add vehicle'); }
  };

  const startEditAuto = (auto) => {
    setEditingAutoId(auto.id);
    setAutoDraft({ ...auto });
  };

  const saveAuto = async (id) => {
    setSaving(true);
    try {
      await api(`/api/clients/${clientId}/autos/${id}`, { method: 'PATCH', body: JSON.stringify(autoDraft) });
      setAutos(p => p.map(x => x.id === id ? { ...x, ...autoDraft } : x));
      setEditingAutoId(null);
    } catch { setError('Save failed'); }
    finally { setSaving(false); }
  };

  const cancelEditAuto = () => setEditingAutoId(null);

  const deleteAuto = async (id) => {
    try {
      await api(`/api/clients/${clientId}/autos/${id}`, { method: 'DELETE' });
      setAutos(p => p.filter(x => x.id !== id));
    } catch { setError('Failed to delete vehicle'); }
  };

  // ─────────────────────────────────────────
  // REC UNIT HANDLERS
  // ─────────────────────────────────────────
  const addRecUnit = async () => {
    try {
      const created = await api(`/api/clients/${clientId}/recreational`, { method: 'POST', body: JSON.stringify(emptyRecUnit()) });
      setRecreationalUnits(p => [...p, created]);
      setEditingRecId(created.id);
      setRecDraft(created);
    } catch { setError('Failed to add unit'); }
  };

  const startEditRecUnit = (unit) => {
    setEditingRecId(unit.id);
    setRecDraft({ ...unit });
  };

  const saveRecUnit = async (id) => {
    setSaving(true);
    try {
      await api(`/api/clients/${clientId}/recreational/${id}`, { method: 'PATCH', body: JSON.stringify(recDraft) });
      setRecreationalUnits(p => p.map(x => x.id === id ? { ...x, ...recDraft } : x));
      setEditingRecId(null);
    } catch { setError('Save failed'); }
    finally { setSaving(false); }
  };

  const cancelEditRecUnit = () => setEditingRecId(null);

  const deleteRecUnit = async (id) => {
    try {
      await api(`/api/clients/${clientId}/recreational/${id}`, { method: 'DELETE' });
      setRecreationalUnits(p => p.filter(x => x.id !== id));
    } catch { setError('Failed to delete unit'); }
  };

  // ─────────────────────────────────────────
  // DRIVER HANDLERS
  // ─────────────────────────────────────────
  const addDriver = async () => {
    try {
      const created = await api(`/api/clients/${clientId}/drivers`, { method: 'POST', body: JSON.stringify({ firstName: '', lastName: '', dateOfBirth: null, gender: '', relationshipToInsured: '', driverStatus: 'Active' }) });
      setDrivers(p => [...p, created]);
      setEditingDriverId(created.id);
      setDriverDraft(created);
    } catch { setError('Failed to add driver'); }
  };

  const startEditDriver = (driver) => {
    setEditingDriverId(driver.id);
    setDriverDraft(driver);
  };

  const saveDriver = async (id) => {
    setSaving(true);
    try {
      await api(`/api/clients/${clientId}/drivers/${id}`, { method: 'PATCH', body: JSON.stringify(driverDraft) });
      setDrivers(p => p.map(x => x.id === id ? { ...x, ...driverDraft } : x));
      setEditingDriverId(null);
    } catch { setError('Save failed'); }
    finally { setSaving(false); }
  };

  const cancelEditDriver = () => setEditingDriverId(null);

  const deleteDriver = async (id) => {
    try {
      await api(`/api/clients/${clientId}/drivers/${id}`, { method: 'DELETE' });
      setDrivers(p => p.filter(x => x.id !== id));
    } catch { setError('Failed to delete driver'); }
  };

  // ─────────────────────────────────────────
  // FILE HANDLERS
  // ─────────────────────────────────────────
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setUploadingFile(true);
    setUploadProgress(0);
    try {
      const uploaded = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const fd = new FormData();
        fd.append('file', file);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) setUploadProgress(Math.round((ev.loaded / ev.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve(JSON.parse(xhr.responseText));
          else reject(new Error('Upload failed'));
        };
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.open('POST', `/api/clients/${clientId}/files`);
        xhr.setRequestHeader('Authorization', `Bearer ${getAuthToken()}`);
        xhr.send(fd);
      });
      setFiles(p => [...p, uploaded]);
      setPendingFile(uploaded);
    } catch { setError('File upload failed'); }
    finally { setUploadingFile(false); setUploadProgress(0); }
  };

  const handleViewFile = (fileId) =>
    window.open(`/api/clients/${clientId}/files/${fileId}/download?token=${getAuthToken()}`, '_blank');

  const handleDownloadFile = (fileId, filename) => {
    const a = document.createElement('a');
    a.href = `/api/clients/${clientId}/files/${fileId}/download?token=${getAuthToken()}`;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleAnalyzeFile = async (fileId) => {
    setAnalyzingFileId(fileId);
    setAnalyzeData(null);
    try {
      const token = getAuthToken();
      const fileRes = await fetch(`/api/clients/${clientId}/files/${fileId}/download?token=${token}`);
      if (!fileRes.ok) throw new Error('Could not fetch file');
      const blob = await fileRes.blob();
      const cd = fileRes.headers.get('content-disposition') || '';
      const nameMatch = cd.match(/filename[^;=\n]*=(['"]?)([^'"\n]*?)\1/);
      const filename = nameMatch ? nameMatch[2] : `file-${fileId}`;
      const file = new File([blob], filename, { type: blob.type });
      const fd = new FormData();
      fd.append('files', file);
      const res = await fetch(`/api/clients/${clientId}/analyze-docs`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error('Analysis failed');
      const extracted = await res.json();
      const patch = {};
      if (extracted.policies?.[0]) {
        const p = extracted.policies[0];
        if (p.policyNumber) patch.policyNumber = p.policyNumber;
        if (p.carrier) patch.carrier = p.carrier;
        if (p.coverageType) patch.coverageType = p.coverageType;
        if (p.effectiveDate) patch.effectiveDate = p.effectiveDate;
        if (p.expirationDate) patch.expirationDate = p.expirationDate;
        if (p.premium) patch.premium = p.premium;
      }
      if (extracted.personal) {
        const p = extracted.personal;
        if (p.dateOfBirth) patch.dateOfBirth = p.dateOfBirth;
        if (p.phone) patch.phone = p.phone;
        if (p.occupation) patch.occupation = p.occupation;
        if (p.employer) patch.employer = p.employer;
        if (p.maritalStatus) patch.maritalStatus = p.maritalStatus;
      }
      if (Object.keys(patch).length) {
        await fetch(`/api/clients/${clientId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken()}` },
          body: JSON.stringify(patch),
        });
        setClient(prev => ({ ...prev, ...patch }));
        setBasicDraft(prev => ({
          ...prev, ...patch,
          dateOfBirth: patch.dateOfBirth ? (formatDate(patch.dateOfBirth) || patch.dateOfBirth) : prev.dateOfBirth,
        }));
      }
      const addedAutos = (extracted.autos || []).filter(a => a.make || a.model);
      const addedProps = (extracted.properties || []).filter(p => p.address);
      if (addedAutos.length) setAutos(prev => [...prev, ...addedAutos]);
      if (addedProps.length) {
        setProperties(prev => [...prev, ...addedProps]);
        const newLossMap = {};
        addedProps.forEach(p => { if (p.id) newLossMap[p.id] = []; });
        setPropertyLosses(prev => ({ ...prev, ...newLossMap }));
      }
      setAnalyzeData({ success: true, fields: patch, autos: addedAutos, properties: addedProps });
    } catch (err) {
      setAnalyzeData({ success: false, error: err.message });
    } finally {
      setAnalyzingFileId(null);
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      const res = await fetch(`/api/clients/${clientId}/files/${fileId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) throw new Error('Delete failed');
      setFiles(p => p.filter(f => f.id !== fileId));
    } catch { setError('Failed to delete file'); }
  };

  // ─────────────────────────────────────────
  // RESEND INTAKE
  // ─────────────────────────────────────────
  const handleResendIntake = async () => {
    setResendingIntake(true);
    try {
      await api(`/api/clients/${clientId}/intake/resend`, { method: 'POST', body: JSON.stringify({ email: resendEmail }) });
      setResendSuccess(true);
      setShowResendForm(false);
      setTimeout(() => setResendSuccess(false), 4000);
    } catch { setError('Failed to send intake form'); }
    finally { setResendingIntake(false); }
  };

  // ─────────────────────────────────────────
  // EARLY RETURNS
  // ─────────────────────────────────────────
  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="animate-spin text-blue-600" size={28} />
    </div>
  );
  if (!client) return null;

  // ─────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────
  return (
    <div className="space-y-5">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-800 transition"
      >
        <ArrowLeft size={16} /> Back to Clients
      </button>

      <HeaderBar
        client={client}
        onUploadFile={handleFileUpload}
        uploadingFile={uploadingFile}
        uploadProgress={uploadProgress}
        showResendForm={showResendForm}
        setShowResendForm={setShowResendForm}
        resendEmail={resendEmail}
        setResendEmail={setResendEmail}
        resendingIntake={resendingIntake}
        resendSuccess={resendSuccess}
        onResendIntake={handleResendIntake}
      />

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <AnalyzeModals
        pendingFile={pendingFile}
        onAnalyze={(id) => { setPendingFile(null); handleAnalyzeFile(id); }}
        onSkipPending={() => setPendingFile(null)}
        analyzingFileId={analyzingFileId}
        analyzeData={analyzeData}
        onCloseResult={() => setAnalyzeData(null)}
      />

      <div className="space-y-4">
        <ClientInfoSection
          client={client}
          users={users}
          editing={editingBasic}
          setEditing={setEditingBasic}
          basicDraft={basicDraft}
          setBasicDraft={setBasicDraft}
          onSave={saveBasic}
          saving={saving}
        />

        <PropertiesSection
          properties={properties}
          propertyLosses={propertyLosses}
          editingPropertyId={editingPropertyId}
          propertyDraft={propertyDraft}
          setPropertyDraft={setPropertyDraft}
          saving={saving}
          onAddProperty={addProperty}
          onStartEditProperty={startEditProperty}
          onSaveProperty={saveProperty}
          onCancelEditProperty={cancelEditProperty}
          onDeleteProperty={deleteProperty}
          onAddLoss={addLoss}
          onUpdateLossField={updateLossField}
          onSaveLoss={saveLoss}
          onDeleteLoss={deleteLoss}
        />

        <VehiclesSection
          client={client}
          autos={autos}
          editingAutoId={editingAutoId}
          autoDraft={autoDraft}
          setAutoDraft={setAutoDraft}
          saving={saving}
          onAddAuto={addAuto}
          onStartEditAuto={startEditAuto}
          onSaveAuto={saveAuto}
          onCancelEditAuto={cancelEditAuto}
          onDeleteAuto={deleteAuto}
        />

        <DriversSection
          drivers={drivers}
          editingDriverId={editingDriverId}
          driverDraft={driverDraft}
          setDriverDraft={setDriverDraft}
          saving={saving}
          onAddDriver={addDriver}
          onStartEditDriver={startEditDriver}
          onSaveDriver={saveDriver}
          onCancelEditDriver={cancelEditDriver}
          onDeleteDriver={deleteDriver}
        />

        <RecreationalSection
          recreationalUnits={recreationalUnits}
          editingRecId={editingRecId}
          recDraft={recDraft}
          setRecDraft={setRecDraft}
          saving={saving}
          onAddRecUnit={addRecUnit}
          onStartEditRecUnit={startEditRecUnit}
          onSaveRecUnit={saveRecUnit}
          onCancelEditRecUnit={cancelEditRecUnit}
          onDeleteRecUnit={deleteRecUnit}
        />

        <LiabilitySection
          client={client}
          editing={editingLiability}
          setEditing={setEditingLiability}
          liabilityDraft={liabilityDraft}
          setLiabilityDraft={setLiabilityDraft}
          saving={saving}
          onSave={saveLiability}
        />

        <DocumentsSection
          files={files}
          uploadingFile={uploadingFile}
          analyzingFileId={analyzingFileId}
          onUploadFile={handleFileUpload}
          onViewFile={handleViewFile}
          onDownloadFile={handleDownloadFile}
          onAnalyzeFile={handleAnalyzeFile}
          onDeleteFile={handleDeleteFile}
          setPendingFile={setPendingFile}
        />
      </div>
    </div>
  );
}